import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import { ArrowLeft } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];

export default function BalanceSheet() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = useMemo(() => new Date(), []);

  const [asOf, setAsOf] = useState(toInputDate(today));
  const [bs, setBS] = useState(null);

  useEffect(() => {
    fetchBS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBS = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await reportsAPI.getBalanceSheet(asOf);
      setBS(data);
    } catch (err) {
      setError(err.message || 'Failed to load Balance Sheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Balance Sheet</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Assets, liabilities, and equity</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FormLabel htmlFor="as-of">As of</FormLabel>
                <Input
                  id="as-of"
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={fetchBS} className="w-full">Refresh</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : bs && (
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-h2 font-semibold mb-4 text-[var(--ds-text-primary)]">Assets</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Bank Accounts</span>
                      <span className="tabular-nums">{currency(bs.assets?.bankTotal || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Accounts Receivable</span>
                      <span className="tabular-nums">{currency(bs.assets?.accountsReceivable || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Total Assets</span>
                      <span className="tabular-nums">{currency(bs.assets?.total || 0)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-h2 font-semibold mb-4 text-[var(--ds-text-primary)]">Liabilities</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Accounts Payable</span>
                      <span className="tabular-nums">{currency(bs.liabilities?.accountsPayable || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Total Liabilities</span>
                      <span className="tabular-nums">{currency(bs.liabilities?.total || 0)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-h2 font-semibold mb-4 text-[var(--ds-text-primary)]">Equity</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Equity</span>
                      <span className="tabular-nums">{currency(bs.equity || 0)}</span>
                    </div>
                    <p className="text-xs text-[var(--ds-text-muted)] mt-2">Simple equity calculation: Assets − Liabilities.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
